import React, { useRef, useEffect, useState } from "react";
import "./EditItemDescription.scss";
import "../../../../components/ModalContent/ModalComponents.scss";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { EmoticonReplacer } from "../../../../utils/emoticonExtension";
import ToolTip from "../../../../components/ToolTip/ToolTip";
import Modal from "../../../../components/Modal/Modal";
import TextColorPicker from "../../../Notes/Modals/TextColorPicker/TextColorPicker";
import EmojiPickerModal from "../../../Notes/Modals/EmojiPickerModal/EmojiPickerModal";

function EditItemDescription({ item, color, onUpdate, onCancel }) {
  const toolTipRef = useRef();
  const modalRef = useRef();
  const titleInputRef = useRef();
  const [title, setTitle] = useState(item?.text || "");

  const showToolTip = (e, arrow, content) => {
    const triggerElement = e.currentTarget;

    if (toolTipRef.current) {
      toolTipRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const openModal = (e, arrow, content) => {
    const triggerElement = e.currentTarget;

    if (modalRef.current) {
      modalRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const closeModal = () => {
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  const handleTextColorPicker = (e) => {
    openModal(
      e,
      "top-center",
      <TextColorPicker
        onSelectColor={(color) => {
          editor?.chain().focus().setColor(color).run();
          closeModal();
        }}
      />,
    );
  };

  const handleEmojiPicker = (e) => {
    openModal(
      e,
      "top-center",
      <EmojiPickerModal
        onSelectEmoji={(emoji) => {
          editor?.commands.insertContent(emoji);
          closeModal();
        }}
      />,
    );
  };

  const editor = useEditor({
    extensions: [StarterKit, TextStyle, Color, EmoticonReplacer],
    content: item?.description || "",
    editable: true,
  });

  // Focus title input when modal opens
  useEffect(() => {
    if (titleInputRef.current) {
      setTimeout(() => {
        titleInputRef.current.focus();
        // Move cursor to end of input
        const length = titleInputRef.current.value.length;
        titleInputRef.current.setSelectionRange(length, length);
      }, 150);
    }
  }, []);

  // Prevent toolbar buttons from stealing focus
  const handleButtonMouseDown = (e) => {
    e.preventDefault();
  };

  // Handle clicks on the editor area to ensure it gets focus
  const handleEditorClick = () => {
    if (editor && !editor.isFocused) {
      editor.commands.focus();
    }
  };

  const handleUpdate = () => {
    if (editor && item && title.trim()) {
      const html = editor.getHTML();
      // Remove empty paragraph tags that TipTap creates by default
      const description = html === "<p></p>" ? "" : html;
      onUpdate(item._id, title.trim(), description);
    }
  };

  return (
    <div className="edit-item-description-content">
      <ToolTip ref={toolTipRef}></ToolTip>

      <div className="title">
        <span>Edit Item</span>
        <button className="close-button" onClick={onCancel}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="modal-form">
        <div className="form-group">
          <label className="form-label">Title</label>
          <input
            ref={titleInputRef}
            type="text"
            className="form-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleUpdate();
              }
            }}
            placeholder="Item title..."
          />
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <div
            className="text-editor-wrapper"
            style={{ borderLeft: color ? `2px solid ${color}` : undefined }}
          >
            <div className="text-editor-buttons">
              {/* Text Formatting */}
              <div
                className="text-editor-button"
                onMouseDown={handleButtonMouseDown}
                onClick={() => editor?.chain().focus().toggleBold().run()}
                onMouseEnter={(e) => showToolTip(e, "top-center", "Bold")}
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "22px" }}
                >
                  format_bold
                </span>
              </div>

              <div
                className="text-editor-button"
                onMouseDown={handleButtonMouseDown}
                onClick={() => editor?.chain().focus().toggleItalic().run()}
                onMouseEnter={(e) => showToolTip(e, "top-center", "Italic")}
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "22px" }}
                >
                  format_italic
                </span>
              </div>

              <div
                className="text-editor-button"
                onMouseDown={handleButtonMouseDown}
                onClick={() => editor?.chain().focus().toggleStrike().run()}
                onMouseEnter={(e) =>
                  showToolTip(e, "top-center", "Strikethrough")
                }
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "22px" }}
                >
                  strikethrough_s
                </span>
              </div>

              <div className="text-editor-separator"></div>

              {/* Lists */}
              <div
                className="text-editor-button"
                onMouseDown={handleButtonMouseDown}
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                onMouseEnter={(e) =>
                  showToolTip(e, "top-center", "Bullet List")
                }
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "22px" }}
                >
                  format_list_bulleted
                </span>
              </div>

              <div
                className="text-editor-button"
                onMouseDown={handleButtonMouseDown}
                onClick={() =>
                  editor?.chain().focus().toggleOrderedList().run()
                }
                onMouseEnter={(e) =>
                  showToolTip(e, "top-center", "Numbered List")
                }
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "22px" }}
                >
                  format_list_numbered
                </span>
              </div>

              <div className="text-editor-separator"></div>

              {/* Headings */}
              <div
                className="text-editor-button"
                onMouseDown={handleButtonMouseDown}
                onClick={() =>
                  editor?.chain().focus().toggleHeading({ level: 2 }).run()
                }
                onMouseEnter={(e) => showToolTip(e, "top-center", "Heading")}
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "22px" }}
                >
                  title
                </span>
              </div>

              <div className="text-editor-separator"></div>

              {/* Text Color */}
              <div
                className="text-editor-button"
                onMouseDown={handleButtonMouseDown}
                onClick={handleTextColorPicker}
                onMouseEnter={(e) => showToolTip(e, "top-center", "Text Color")}
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "22px" }}
                >
                  format_color_text
                </span>
              </div>

              {/* Emoji */}
              <div
                className="text-editor-button"
                onMouseDown={handleButtonMouseDown}
                onClick={handleEmojiPicker}
                onMouseEnter={(e) => showToolTip(e, "top-center", "Emoji")}
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "22px" }}
                >
                  mood
                </span>
              </div>

              <div className="text-editor-separator"></div>

              {/* Code and Quote */}
              <div
                className="text-editor-button"
                onMouseDown={handleButtonMouseDown}
                onClick={() => {
                  if (!editor) return;

                  const { from, to } = editor.state.selection;
                  const selectedText = editor.state.doc.textBetween(
                    from,
                    to,
                    "\n",
                  );

                  // If we have selected text across multiple paragraphs
                  if (selectedText && selectedText.includes("\n")) {
                    editor
                      .chain()
                      .focus()
                      .deleteSelection()
                      .insertContent({
                        type: "codeBlock",
                        content: [{ type: "text", text: selectedText }],
                      })
                      .run();
                  } else {
                    // Normal toggle for single line or empty
                    editor.chain().focus().toggleCodeBlock().run();
                  }
                }}
                onMouseEnter={(e) => showToolTip(e, "top-center", "Code Block")}
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "22px" }}
                >
                  code
                </span>
              </div>

              <div
                className="text-editor-button"
                onMouseDown={handleButtonMouseDown}
                onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                onMouseEnter={(e) => showToolTip(e, "top-center", "Quote")}
                onMouseLeave={() => toolTipRef.current?.close()}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "22px" }}
                >
                  format_quote
                </span>
              </div>
            </div>

            <div className="tiptap-editor" onClick={handleEditorClick}>
              <EditorContent editor={editor} />
            </div>
          </div>
        </div>
      </div>

      <div className="modal-actions">
        <button className="modal-button cancel" onClick={onCancel}>
          Cancel
        </button>
        <button className="modal-button primary" onClick={handleUpdate}>
          Update
        </button>
      </div>

      <Modal ref={modalRef} />
    </div>
  );
}

export default EditItemDescription;
